import 'regenerator-runtime/runtime';
import 'core-js/stable';

import { createApp } from 'vue';
import Manager from './core/Manager';
import App from './core/App.vue';
import RouterFactory from './core/RouterFactory';
import i18n from './locale/i18n';
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import 'reset.css/reset.css';
import vuetify from './core/vuetify';
import './style.scss';
import TextfieldComponent from './frontendkit/components/TextfieldComponent.vue';
import SelectComponent from './frontendkit/components/SelectComponent.vue';
import RadioComponent from './frontendkit/components/RadioComponent.vue';
import CheckboxComponent from './frontendkit/components/CheckboxComponent.vue';
import SliderComponent from './frontendkit/components/SliderComponent.vue';
import 'gitart-vue-dialog/dist/style.css';
import { GDialog } from 'gitart-vue-dialog';
import quasar from './lib/quasar/src/vue-plugin';
import * as quasarComponents from './lib/quasar/src/index.prod.js';

import MenuComponent from './components/MenuComponent.vue';
import MenuListItemComponent from './components/MenuListItemComponent.vue';
import OverlayComponent from './components/OverlayComponent.vue';
import DialogComponent from './components/DialogComponent.vue';
import IconComponent from './components/IconComponent.vue';
import AvatarComponent from './components/AvatarComponent.vue';
import PageNotFound from './core/modules/page-not-found/PageNotFound.vue';
import FeedbackComponent from '../lib/mentorkit/FeedbackComponent.vue';

declare const SENTRY_ENVIRONMENT: string;
declare const SENTRY_DSN: string;

document.addEventListener('DOMContentLoaded', async () => {

  let error404 = false;
  await Manager.getInstance().load().catch((e) => {
    if (e.response?.status === 404) {
      error404 = true;
    }
  });

  const app = createApp(App);
  Manager.getInstance().app = app;
  Manager.getInstance().i18n = i18n;
  app.use(i18n);
  const router = RouterFactory.create();

  if (SENTRY_DSN) {
    Sentry.init({
      app,
      environment: SENTRY_ENVIRONMENT,
      dsn: SENTRY_DSN,
      logErrors: true,
      integrations: [
        new BrowserTracing({
          routingInstrumentation: Sentry.vueRouterInstrumentation(router),
          tracePropagationTargets: [window.location.hostname, /^\//],
        }),
        new Sentry.Replay(),
      ],
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 1.0,
    });
  }

  if (!error404) {
    app.use(router);
  }
  app.use(vuetify);
  app.use(quasar);
  Object.keys(quasarComponents).forEach((key) => {
    app.component(key, quasarComponents[key]);
  });
  app.component('GDialog', GDialog);

  app.component('TextfieldComponent', TextfieldComponent);
  app.component('SelectComponent', SelectComponent);
  app.component('RadioComponent', RadioComponent);
  app.component('CheckboxComponent', CheckboxComponent);
  app.component('SliderComponent', SliderComponent);
  app.component('MenuComponent', MenuComponent);
  app.component('MenuListItemComponent', MenuListItemComponent);
  app.component('OverlayComponent', OverlayComponent);
  app.component('DialogComponent', DialogComponent);
  app.component('Avatar', AvatarComponent);
  app.component('Icon', IconComponent);
  app.component('FeedbackComponent', FeedbackComponent);

  const params = new URLSearchParams(window.location.search);
  const showIds = params.get('showIds');
  if (showIds === 'true') {
    app.provide('showIds', showIds);
  }

  app.mount('.body-div');
  if (error404) {
    document.body.classList.add('loading');
    const pageNotFound = createApp(PageNotFound);
    pageNotFound.use(i18n);
    pageNotFound.mount('.body-div main');
  }
});
